import { typeToModuleId } from '@mentimeter/compatibility/backward-compatibility/static-content';
import { useIsAuthorized } from '@mentimeter/core-global-state';
import {
  useActiveQuestionId,
  useAppRouterIds,
} from '@mentimeter/core-hooks/use-ids';
import { getSlideById } from '@mentimeter/editor-essentials/calculations';
import { CONTENT_SLIDE_MODULE_IDS } from '@mentimeter/editor-essentials/slide-types';
import { useEditorRouter } from '@mentimeter/editor-essentials/hooks';
import { KeyboardHandlerProvider } from '@mentimeter/editor-essentials/utils';
import {
  useActiveSidebarPaneStore,
  useOverviewVisibleStore,
} from '@mentimeter/editor-state';
import { trackClickedCommentIcon } from '@mentimeter/editor-tracking';
import type { TrackingContext } from '@mentimeter/http-clients';
import { useSearchParams } from '@mentimeter/next-navigation';
import { useDeviceChecker } from '@mentimeter/ragnar-device';
import { type UserT } from '@mentimeter/user';
import type {
  SeriesWithSlideDeck,
  QuestionWithSlide as ViewOnlyQuestionWithSlide,
} from '@mentimeter/view-only-schema/api-types-overrides';
import { OverviewToggleButton } from '@mentimeter/editor-overview-ui';
import { OverviewListViewOnly } from '@mentimeter/editor-overview-data-handlers';
import { EditorThemeProvider } from 'features/core/edit_view/application/EditorThemeProvider';
import { useRegisterViewSeries } from 'features/core/edit_view/application/shared/use-register-view-series';
import { HeaderViewOnly } from 'features/core/editor/Header/view-only/HeaderViewOnly';
import { SideBarViewOnly } from 'features/core/editor/Sidebar/view-only/SideBarViewOnly';
import { setupRegion } from 'lib/http/region-setup';
import { useEffect } from 'react';
import {
  COLLABORATOR_STATUS_COMMENT,
  COLLABORATOR_STATUS_VIEW,
  type CollaboratorStatus,
} from 'utils/constants';
import { Intercom } from 'utils/Intercom';
import { useIsSidebarPaneOpen } from 'features/core/editor/Sidebar/utils';
import { EditorLayout } from 'app/(tracked)/[seriesId]/(collaborator)/(editor)/editor-layout-components/EditorLayout';
import { EditorMobileLayout } from 'app/(tracked)/[seriesId]/(collaborator)/(editor)/editor-layout-components/EditorMobileLayout';
import { PageLayout } from '../../../(collaborator)/(editor)/editor-layout-components/PageLayout';
import { trackClickedSlideThumbnail } from '../../trackViewOnlyEditor';
import { ViewOnlyCanvas } from './ViewOnlyCanvas';
import { ViewOnlyFooter } from './ViewOnlyFooter';

export const ViewOnlyPage = ({
  user,
  series,
  questions = [],
  collaboratorStatus,
  questionId,
  trackingContext,
}: {
  user: UserT | null;
  series: SeriesWithSlideDeck;
  questions: Array<ViewOnlyQuestionWithSlide>;
  collaboratorStatus: CollaboratorStatus;
  questionId: string;
  trackingContext: TrackingContext;
}) => {
  const { isMobile, isSmallDevice } = useDeviceChecker();
  const { navigateToSlide } = useEditorRouter();
  const activeQuestionId = useActiveQuestionId();

  const [activeQuestion, activeQuestionIndex] = getSlideById(
    activeQuestionId,
    questions,
  );

  const slideModuleId = activeQuestion?.staticContent?.type
    ? typeToModuleId(activeQuestion?.staticContent?.type)
    : activeQuestion?.module_id;
  const shouldAnimate = Boolean(
    slideModuleId && !CONTENT_SLIDE_MODULE_IDS.includes(slideModuleId),
  );

  const isAuthorized = useIsAuthorized();
  useRegisterViewSeries(series.id, isAuthorized);

  const activeSidebarPane = useActiveSidebarPaneStore(
    (state) => state.activeSidebarPane,
  );
  const setActiveSidebarPane = useActiveSidebarPaneStore(
    (state) => state.setActiveSidebarPane,
  );
  const shouldSidebarBeInitiallyClosed = isSmallDevice;
  const searchParams = useSearchParams();
  const hasCommentSearchParam = searchParams.get('comment');
  const userIsViewCollaborator =
    collaboratorStatus === COLLABORATOR_STATUS_VIEW;
  const userIsCommentCollaborator =
    collaboratorStatus === COLLABORATOR_STATUS_COMMENT;

  useEffect(() => {
    if (shouldSidebarBeInitiallyClosed) {
      setActiveSidebarPane('closed');
    }
  }, [shouldSidebarBeInitiallyClosed, setActiveSidebarPane]);

  useEffect(() => {
    if (
      !shouldSidebarBeInitiallyClosed &&
      userIsCommentCollaborator &&
      hasCommentSearchParam &&
      activeSidebarPane === 'initial'
    ) {
      setActiveSidebarPane('comments');
    }
  }, [
    shouldSidebarBeInitiallyClosed,
    activeSidebarPane,
    setActiveSidebarPane,
    userIsCommentCollaborator,
    hasCommentSearchParam,
  ]);
  setupRegion(series.id);
  useAppRouterIds({ seriesId: series.id, questionId });

  const handleClickSlideThumbnail = (questionId: string) => {
    navigateToSlide(questionId);
    trackClickedSlideThumbnail(
      user,
      trackingContext,
      series,
      collaboratorStatus,
    );
  };

  const isOverviewVisible = useOverviewVisibleStore(
    (state) => state.isOverviewVisible,
  );
  const setIsOverviewVisible = useOverviewVisibleStore(
    (state) => state.setIsOverviewVisible,
  );

  const handleOverviewVisibleToggle = () =>
    setIsOverviewVisible(!isOverviewVisible);

  const compactMode = isMobile;

  const isPaneOpen = useIsSidebarPaneOpen();

  const buttonSize = !isSmallDevice && !isMobile ? 'default' : 'compact';
  const isVisitor = Boolean(!user);

  return (
    <EditorThemeProvider>
      <Intercom />
      <KeyboardHandlerProvider
        questionIds={questions.map((question) => question.id)}
        activeQuestionIndex={activeQuestionIndex}
        activateQuestion={handleClickSlideThumbnail}
      >
        <PageLayout
          elementId="viewonly-editor-container"
          header={
            <HeaderViewOnly
              seriesId={series.id}
              seriesName={series.name}
              trackingContext={trackingContext}
              userIsCollaborator={
                userIsCommentCollaborator || userIsViewCollaborator
              }
              user={user}
              badgeLabel={userIsCommentCollaborator ? 'Comment' : 'View'}
            />
          }
          content={
            compactMode ? (
              <EditorMobileLayout
                isPaneOpen={isPaneOpen}
                canvas={
                  <ViewOnlyCanvas
                    questionId={questionId}
                    series={series}
                    shouldAnimate={shouldAnimate}
                  />
                }
                actionsAlign="bottom"
                actions={
                  (isVisitor || userIsCommentCollaborator) && (
                    <ViewOnlyFooter
                      isVisitor={isVisitor}
                      buttonSize={buttonSize}
                      seriesId={series.id}
                      trackingContext={trackingContext}
                      isCommentDrawerOpen={activeSidebarPane === 'comments'}
                      isShared={Boolean(series.workspace_id)}
                      currentPlan={user?.features.type || 'registered'}
                      roleInWorkspace={user?.role || 'user'}
                    />
                  )
                }
                slidesOverview={
                  <OverviewListViewOnly
                    seriesTheme={series.theme}
                    activeQuestionId={activeQuestionId}
                    overviewListItems={questions}
                    userIsCommentCollaborator={userIsCommentCollaborator}
                    onClickSlideThumbnail={handleClickSlideThumbnail}
                    onClickCommentIcon={() => {
                      setActiveSidebarPane('comments');
                      trackClickedCommentIcon({
                        role: 'Collaborator',
                        context: trackingContext,
                        seriesId: series.id,
                        isSharedPresentation: series.co_edited,
                        hasResults: series.has_results,
                        permission: collaboratorStatus,
                      });
                    }}
                    compactMode={compactMode}
                  />
                }
              />
            ) : (
              <EditorLayout
                isSidePaneOpen={isPaneOpen}
                isOverviewVisible={isOverviewVisible}
                slideOverviewToggle={
                  <OverviewToggleButton
                    controls="editor-overview-container"
                    expanded={isOverviewVisible}
                    label={`Slide overview (slide ${
                      activeQuestionIndex + 1
                    } of ${questions.length})`}
                    onClick={handleOverviewVisibleToggle}
                  >
                    {activeQuestionIndex + 1} / {questions.length}
                  </OverviewToggleButton>
                }
                slidesOverview={
                  <OverviewListViewOnly
                    seriesTheme={series.theme}
                    activeQuestionId={activeQuestionId}
                    overviewListItems={questions}
                    userIsCommentCollaborator={userIsCommentCollaborator}
                    onClickSlideThumbnail={handleClickSlideThumbnail}
                    onClickCommentIcon={() => {
                      setActiveSidebarPane('comments');
                      trackClickedCommentIcon({
                        role: 'Collaborator',
                        context: trackingContext,
                        seriesId: series.id,
                        isSharedPresentation: series.co_edited,
                        hasResults: series.has_results,
                        permission: collaboratorStatus,
                      });
                    }}
                    compactMode={compactMode}
                  />
                }
                canvas={
                  <ViewOnlyCanvas
                    questionId={questionId}
                    series={series}
                    shouldAnimate={shouldAnimate}
                  />
                }
                sidebar={
                  activeQuestion && userIsCommentCollaborator ? (
                    <SideBarViewOnly
                      user={user}
                      workspaceId={series.workspace_id}
                      activeQuestionId={activeQuestion.id}
                      activeQuestionModuleId={activeQuestion.module_id}
                      activeQuestionAdminKey={activeQuestion.slideAdminKey}
                      userIsCommentCollaborator={userIsCommentCollaborator}
                    />
                  ) : null
                }
              />
            )
          }
        />
      </KeyboardHandlerProvider>
    </EditorThemeProvider>
  );
};
